import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { applyActionCode, checkActionCode, confirmPasswordReset, isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';
import { auth, db } from '../../../firebase';
import { doc, updateDoc, getDoc, arrayUnion, deleteField, serverTimestamp } from 'firebase/firestore';
import { EyeIcon, EyeOffIcon } from '@heroicons/react/solid';
import PasswordRequirements from '../../../components/auth/PasswordRequirements';
import { validatePasswordString } from '../../../hooks/auth/usePasswordValidation';
import InfoTooltip from '../../../components/InfoTooltip';
import { getBackgroundGradient } from '../../../utils/environmentTheme';
import { getServiceUserProfileDocRef } from '../../../utils/informantFirestorePaths';
import { resetCompletePath, rmsPathFromContinueUrl, signInPageForFailedLink } from '../../../rms/utils/signInRoutes';
import { SIGN_IN_LINK_LIFETIME } from '../../../rms/constants/signInLink';

/**
 * Validate that a redirect URL is same-origin to prevent open-redirect attacks.
 * An attacker could craft a link with continueUrl=https://evil.com — after sign-in
 * the user would be redirected off-site. We only allow URLs whose origin matches
 * the current page, or simple path-only strings (e.g. "/rms/dashboard").
 */
const isSafeRedirect = (url) => {
  if (!url) return false;
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin;
  } catch { return false; }
};

/**
 * Map Firebase Auth error codes from password reset to user-friendly messages.
 * Raw Firebase messages (e.g. "Firebase: Error (auth/expired-action-code).")
 * are technical and confusing for end users.
 */
const friendlyResetError = (code) => {
  switch (code) {
    case 'auth/expired-action-code':
      return 'This password reset link has expired. Please request a new one.';
    case 'auth/invalid-action-code':
      return 'This link has already been used or is no longer valid. Please request a new password reset.';
    case 'auth/weak-password':
      return 'The password is too weak. Please choose a stronger password.';
    default:
      return 'Something went wrong resetting your password. Please try again.';
  }
};

export default function VerifyAction() {
  const [status, setStatus] = useState('verifying');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [suggestedPortal, setSuggestedPortal] = useState('provider'); // 'provider' | 'patient'
  // Single source of truth for what portal we will actually use for both
  // messaging and navigation. It is computed from freshest claims or URL hint.
  const [resolvedPortal, setResolvedPortal] = useState(null); // 'provider' | 'patient' | null
  const [successKind, setSuccessKind] = useState('generic'); // 'verifyEmail' | 'resetPassword' | 'revertMfa' | 'generic'
  // Tracks whether the "Confirm Removal" button has been clicked to prevent
  // double-submission and show a loading spinner during the applyActionCode call.
  const [isRevertingMfa, setIsRevertingMfa] = useState(false);
  const [revertMfaError, setRevertMfaError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  const [showPassword, setShowPassword] = useState(true);
  const [showConfirmPassword, setShowConfirmPassword] = useState(true);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [passwordMatch, setPasswordMatch] = useState(null);
  const [supportCtx, setSupportCtx] = useState({ userId: '', organisation: '' });

  // Guard against duplicate handleAction executions. The handleAction effect depends
  // on resolvedPortal/suggestedPortal which change shortly after mount (portal
  // resolution effect). Without this guard, the effect re-fires and a second
  // signInWithEmailLink call races the first — the second always fails because the
  // oobCode was already consumed, resulting in a spurious "link expired" error.
  const actionHandledRef = useRef(false);

  // Cross-device email prompt state (replaces window.prompt which is blocked in
  // many browsers). When a user opens an email sign-in link on a different device
  // or browser than the one that initiated the request, we have no stored email.
  // We show an in-page form instead of the unreliable window.prompt.
  const [signInEmailInput, setSignInEmailInput] = useState('');
  const [signInBusy, setSignInBusy] = useState(false);
  // Persist the full page href and continueUrl across the async gap between
  // "we need an email" (handleAction exits) and "user submitted the form".
  const signInHrefRef = useRef('');
  const signInContinueUrlRef = useRef('');
  // The account a password-reset code belongs to, read when the code is
  // checked, so an RMS sign-in page can be opened with it filled in.
  const resetEmailRef = useRef('');

  // Ensure consistent vertical spacing at the top of the panel:
  // in dev/staging we render an environment banner that provides top space;
  // in production (no banner), we add top margin to the logo so it doesn't
  // touch the rounded top edge of the card.
  const showEnvBanner = (import.meta.env.VITE_ENVIRONMENT === "development" || import.meta.env.VITE_ENVIRONMENT === "staging");

  // Get the action code and mode from the URL
  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const actionCode = urlParams.get('oobCode');
  const mode = urlParams.get('mode');
  const accountTypeFromUrlHint = urlParams.get('at');
  // The password-reset link includes the user's email as a base64 ?e= param
  // (appended by our backend when generating the link). We forward it to the
  // sign-in page redirect so the email field can be pre-filled.
  const encodedEmailFromUrl = urlParams.get('e');
  const continueUrlParam = urlParams.get('continueUrl') || '';
  // An RMS password reset (school staff, TDI-702): its continueUrl is an
  // /rms/ address on this site. Everything else keeps today's behaviour.
  const isRmsResetLink = mode === 'resetPassword' && Boolean(rmsPathFromContinueUrl(continueUrlParam));

  /**
   * Detect if this is an RMS flow to apply alternative theming.
   * RMS uses a light gray background with minimal styling, whereas the
   * ADHD Assessment portal uses a purple gradient background.
   * 
   * Detection criteria:
   * - continueUrl contains '/rms/' (RMS redirect path)
   * - mode is 'signIn' (passwordless sign-in is primarily used by RMS)
   */
  const isRmsFlow = useMemo(() => {
    const continueUrl = urlParams.get('continueUrl') || '';
    if (continueUrl.includes('/rms/')) return true;
    if (mode === 'signIn') return true;
    return false;
  }, [urlParams, mode]);

  // Portal inference strategy used throughout this screen
  // -----------------------------------------------------
  // We may not have a signed-in user when a link is opened from email. To keep
  // redirects consistent, we resolve a single portal source of truth as follows:
  // 1) Prefer URL 'at' (account type) hint when present (our custom emails add this)
  // 2) Otherwise, if a user is signed in, derive from their claims
  // 3) Otherwise, default to provider
  const determinePortal = useCallback(async () => {
    const hint = (accountTypeFromUrlHint || '').toLowerCase();
    if (hint) {
      if (hint.includes('service') || hint.includes('patient')) return 'patient';
      if (hint.includes('provider') || hint.includes('clinician') || hint.includes('support') || hint.includes('org')) return 'provider';
    }
    try {
      const u = auth?.currentUser;
      if (u) {
        const token = await u.getIdTokenResult(true);
        const accountType = token?.claims?.accountType;
        const userType = token?.claims?.userType;
        if (accountType === 'Service User' || userType === 'Informant' || userType === 'Patient') {
          return 'patient';
        }
        return 'provider';
      }
    } catch (_) {}
    return 'provider';
  }, [accountTypeFromUrlHint]);

  // Pre-resolve portal early so both UI messages and redirects agree
  useEffect(() => {
    let mounted = true;
    (async () => {
      const portal = await determinePortal();
      if (mounted) setResolvedPortal(portal);
    })();
    return () => { mounted = false; };
  }, [determinePortal]);

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    if (confirmPassword) {
      setPasswordMatch(confirmPassword === newPassword);
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);
    setPasswordMatch(newConfirmPassword === password);
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError('');

    const { errors } = validatePasswordString(password);
    if (errors.length > 0) {
      setError(errors.join(". "));
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      await confirmPasswordReset(auth, actionCode, password);

      setSuccessKind('resetPassword');
      setStatus('success');
      
      // Redirect to the appropriate sign-in page with the email pre-filled
      // so the user can sign in immediately without re-typing it.
      // RMS links go back to their own sign-in page, which shows "Your
      // password is set" with the email filled in (TDI-702).
      let resetEmail = resetEmailRef.current;
      if (!resetEmail && encodedEmailFromUrl) {
        try { resetEmail = atob(encodedEmailFromUrl); } catch (_) { resetEmail = ''; }
      }
      const rmsDestination = isRmsResetLink ? resetCompletePath(continueUrlParam, resetEmail) : null;
      setTimeout(() => {
        if (rmsDestination) {
          navigate(rmsDestination);
          return;
        }
        const portal = resolvedPortal || suggestedPortal || 'provider';
        const basePath = portal === 'patient' ? '/service-user/login' : '/login';
        const emailSuffix = encodedEmailFromUrl ? `?e=${encodeURIComponent(encodedEmailFromUrl)}` : '';
        navigate(`${basePath}${emailSuffix}`);
      }, 3000);
    } catch (err) {
      console.error('Error resetting password:', err);
      setError(friendlyResetError(err?.code));
    }
  };

  // ---------------------------------------------------------------------------
  // MFA revert confirmation handler
  // ---------------------------------------------------------------------------
  // Called when the user explicitly clicks "Confirm Removal" on the
  // confirmRevertMfa screen. This is the only path that actually processes
  // the action code — page load alone will NOT remove MFA.
  const handleConfirmRevertMfa = async () => {
    if (isRevertingMfa) return;
    setIsRevertingMfa(true);
    setRevertMfaError('');

    // Best-effort Firestore cleanup for both provider and service user
    // documents. Writes an audit trail entry and clears stale MFA flags.
    const cleanupFirestore = async (source) => {
      try {
        const u = auth?.currentUser;
        if (!u) return;
        const portal = resolvedPortal || (await determinePortal());

        const buildAuditEntry = (prev) => ({
          action: 'mfa_removed',
          source,
          at: new Date(),
          previous: {
            isMFASetUp: prev?.isMFASetUp ?? null,
            mfaMethod: prev?.mfaMethod ?? null,
            mfaPhoneNumber: prev?.mfaPhoneNumber ?? null,
            mfaSetupAt: prev?.mfaSetupAt ?? prev?.mfaSetUpAt ?? null,
          },
        });

        const updateMfaDoc = async (docRef) => {
          const snap = await getDoc(docRef);
          if (!snap.exists()) return;
          const prev = snap.data() || {};
          await updateDoc(docRef, {
            isMFASetUp: false,
            mfaRemovedAt: serverTimestamp(),
            mfaPhoneNumber: deleteField(),
            mfaMethod: deleteField(),
            mfaAuditTrail: arrayUnion(buildAuditEntry(prev)),
          });
        };

        if (portal === 'provider') {
          await updateMfaDoc(doc(db, 'clinicians', u.uid));
        } else {
          // Service user (patient/informant) — resolve correct collection via claims
          const idTokenResult = await u.getIdTokenResult();
          const profileRef = getServiceUserProfileDocRef(u.uid, idTokenResult.claims);
          await updateMfaDoc(profileRef);
        }
      } catch (fsErr) {
        console.error('Failed to update Firestore after MFA removal:', fsErr);
      }
    };

    try {
      await applyActionCode(auth, actionCode);
      await cleanupFirestore('email_link');

      setSuccessKind('revertMfa');
      setStatus('success');
      setTimeout(() => {
        (async () => {
          const portal = resolvedPortal || (await determinePortal());
          navigate(portal === 'patient' ? '/service-user/login' : '/login');
        })();
      }, 3000);
    } catch (revertErr) {
      const code = revertErr?.code || '';

      // Already-used codes indicate a previous visit (possibly by a scanner
      // before this fix was deployed, or the user clicking twice). The factor
      // is already removed, so we treat this as success.
      if (code === 'auth/invalid-action-code') {
        console.warn('Revert MFA action code already used; treating as success');
        await cleanupFirestore('email_link_already_applied');
        setSuccessKind('revertMfa');
        setStatus('success');
        setTimeout(() => {
          (async () => {
            const portal = resolvedPortal || (await determinePortal());
            navigate(portal === 'patient' ? '/service-user/login' : '/login');
          })();
        }, 3000);
        return;
      }

      if (code === 'auth/expired-action-code') {
        setRevertMfaError(
          'This link has expired. Please sign in and set up 2-step verification again, or contact your administrator.'
        );
      } else {
        console.error('Unexpected error reverting MFA:', revertErr);
        setRevertMfaError(
          'Something went wrong. Please try again or contact your administrator.'
        );
      }
    } finally {
      setIsRevertingMfa(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Cross-device sign-in email submission handler
  // ---------------------------------------------------------------------------
  // Called when the user submits the in-page email form (signInNeedsEmail status).
  // This replaces the previous window.prompt fallback which was unreliable.
  const handleCompleteSignInWithEmail = useCallback(async () => {
    if (!signInEmailInput || signInBusy) return;
    setSignInBusy(true);
    setError('');
    try {
      await signInWithEmailLink(auth, signInEmailInput, signInHrefRef.current);
      try { window.localStorage.removeItem('emailForSignIn'); } catch (_) {}

      const continueUrl = signInContinueUrlRef.current;
      if (continueUrl && isSafeRedirect(continueUrl)) {
        window.location.assign(continueUrl);
        return;
      }
      const portal = resolvedPortal || (await determinePortal()) || 'provider';
      navigate(portal === 'patient' ? '/service-user' : '/dashboard');
    } catch (e) {
      const code = e?.code || '';
      console.error('Email link sign-in failed:', e);

      if (code === 'auth/multi-factor-auth-required') {
        setStatus('signInMfaBlocked');
      } else if (code === 'auth/invalid-email') {
        setError('The email address doesn\'t match this sign-in link. Please check and try again.');
      } else {
        setError('Could not complete sign-in. Please check the email and try again.');
      }
    } finally {
      setSignInBusy(false);
    }
  }, [signInEmailInput, signInBusy, resolvedPortal, determinePortal, navigate]);

  useEffect(() => {
    const handleEmailVerification = async (actionCode) => {
      try {
        await applyActionCode(auth, actionCode);
        
        if (auth.currentUser) {
          await auth.currentUser.getIdToken(true);
          await auth.currentUser.reload();
          
          const freshUser = auth.currentUser;
          
          if (freshUser) {
            // Write email-verified flag to the correct Firestore collection
            // based on portal (provider → clinicians, patient → patients).
            try {
              const portal = resolvedPortal || (await determinePortal());
              const collection = portal === 'patient' ? 'patients' : 'clinicians';
              const userRef = doc(db, collection, freshUser.uid);
              await updateDoc(userRef, {
                isEmailVerified: true,
                emailVerifiedAt: new Date().toISOString()
              });
            } catch (firestoreError) {
              console.error("Error updating Firestore:", firestoreError);
            }
          }
        }
        
        setSuccessKind('verifyEmail');
        setStatus('success');
        setTimeout(() => {
          (async () => {
            if (auth.currentUser && auth.currentUser.emailVerified) {
              const portal = resolvedPortal || (await determinePortal());
              navigate(portal === 'patient' ? '/service-user/set-up-mfa' : '/set-up-mfa');
            }
          })();
        }, 3000);
      } catch (error) {
        console.error('Error verifying email:', error);
        setStatus('error');
      }
    };

    const handleAction = async () => {
      if (actionHandledRef.current) return;
      actionHandledRef.current = true;

      try {        
        if (!actionCode) {
          console.error("Missing action code");
          setStatus('error');
          return;
        }
          
        // The portal from the URL hint alone, with no network call.
        const portalFromHint = () => {
          const h = (accountTypeFromUrlHint || '').toLowerCase();
          return (h.includes('service') || h.includes('patient')) ? 'patient' : 'provider';
        };

        // TDI-506: an email-link sign-in code is not one checkActionCode can
        // read — signInWithEmailLink uses a different mechanism entirely — so
        // for mode 'signIn' the call below always fails and its result is
        // thrown away. Waiting for that round trip to time out is dead time on
        // the one screen a parent stares at after tapping the link in their
        // email, and the catch already falls back to the hint. Skip it.
        if (mode === 'signIn') {
          setSuggestedPortal(portalFromHint());
        } else {
          // Check the action code first and infer portal (URL hint > claims > default)
          try {
            const codeInfo = await checkActionCode(auth, actionCode);
            if (mode === 'resetPassword') resetEmailRef.current = codeInfo?.data?.email || '';
            const hint = (accountTypeFromUrlHint || '').toLowerCase();
            if (hint.includes('service') || hint.includes('patient')) {
              setSuggestedPortal('patient');
            } else if (hint.includes('provider') || hint.includes('clinician') || hint.includes('support') || hint.includes('org')) {
              setSuggestedPortal('provider');
            } else if (auth.currentUser) {
              try {
                const token = await auth.currentUser.getIdTokenResult(true);
                const userType = token?.claims?.userType;
                const accountType = token?.claims?.accountType;
                if (userType === 'Informant' || userType === 'Patient' || accountType === 'Service User') {
                  setSuggestedPortal('patient');
                } else {
                  setSuggestedPortal('provider');
                }
              } catch (_) {
                setSuggestedPortal('provider');
              }
            } else {
              setSuggestedPortal('provider');
            }
          } catch (checkError) {
            // For MFA removal links it is common for clients to prefetch or users
            // to re-open links. If checkActionCode fails here, we continue for
            // 'revertSecondFactorAddition', where the actual action may still
            // succeed. ('signIn' never reaches this branch — see above.)
            if (mode !== 'revertSecondFactorAddition') {
              setStatus('error');
              return;
            }
            setSuggestedPortal(portalFromHint());
          }
        }
        
        // Handle different action modes
        switch (mode) {
          case 'signIn': {
            // Passwordless email-link sign-in (currently used by RMS service users)
            try {
              const href = typeof window !== 'undefined' ? window.location.href : '';
              if (!isSignInWithEmailLink(auth, href)) {
                setStatus('error');
                return;
              }
              
              // Try to extract email from multiple sources in order of preference:
              // 1. Encoded email in continueUrl (e.g., /rms/sign-in?e=<base64>)
              // 2. localStorage (set during sign-in initiation on same device)
              // 3. Show an in-page form (cross-device fallback)
              let email = '';
              const continueUrl = urlParams.get('continueUrl');
              
              if (continueUrl) {
                try {
                  const continueUrlObj = new URL(continueUrl);
                  const encodedEmail = continueUrlObj.searchParams.get('e');
                  if (encodedEmail) {
                    email = atob(encodedEmail);
                  }
                } catch (urlParseError) {
                  console.warn('Could not parse continueUrl for email:', urlParseError);
                }
              }
              
              if (!email) {
                try {
                  email = window.localStorage.getItem('emailForSignIn') || '';
                } catch (_) {}
              }
              
              // No email available — show in-page email form instead of window.prompt
              // (window.prompt is blocked in many browsers/contexts)
              if (!email) {
                signInHrefRef.current = href;
                signInContinueUrlRef.current = continueUrl || '';
                setStatus('signInNeedsEmail');
                return;
              }
              
              await signInWithEmailLink(auth, email, href);
              try { window.localStorage.removeItem('emailForSignIn'); } catch (_) {}
              
              // Redirect to continueUrl if provided and same-origin, else default portal
              if (continueUrl && isSafeRedirect(continueUrl)) {
                window.location.assign(continueUrl);
                return;
              }
              const portal = resolvedPortal || (await determinePortal()) || 'provider';
              navigate(portal === 'patient' ? '/service-user' : '/dashboard');
              return;
            } catch (e) {
              // Differentiate Firebase error codes so users see actionable messages
              // instead of a blanket "link expired" for every failure type.
              const code = e?.code || '';
              console.error('Email link sign-in failed:', e);

              if (code === 'auth/multi-factor-auth-required') {
                // Account has MFA enrolled — passwordless sign-in can't complete the
                // second factor challenge. Direct the user to the password sign-in page.
                setStatus('signInMfaBlocked');
              } else {
                // For expired/invalid codes and other errors, use the generic error
                // flow which auto-redirects to the appropriate sign-in page.
                setStatus('error');
              }
              return;
            }
          }
          case 'verifyEmail':
            await handleEmailVerification(actionCode);
            break;
          case 'revertSecondFactorAddition':
            // CRITICAL: Do NOT auto-call applyActionCode here.
            //
            // When MFA is enrolled, Firebase sends an automatic notification email
            // containing a "revert second factor" link that points to this page.
            // Email security scanners (Outlook SafeLinks, Google Safe Browsing,
            // Mimecast, Proofpoint, etc.) pre-fetch links in incoming emails to
            // check for malicious content. If we process the action code on page
            // load, the scanner visit silently removes the user's MFA factor.
            //
            // Instead, we show a confirmation screen requiring the user to click
            // a button. Scanners don't click buttons, so this breaks the cycle.
            // See: https://github.com/firebase/firebase-js-sdk/discussions/8585
            setStatus('confirmRevertMfa');
            break;
          case 'resetPassword':                  
            setStatus('resetPassword');
            break;
          default:
            console.error("Invalid mode");
            setStatus('error');
        }
      } catch (error) {
        console.error('Error:', error);
        setStatus('error');
      }
    };

    handleAction();
  }, [navigate, location.search, actionCode, mode, suggestedPortal, accountTypeFromUrlHint, determinePortal, resolvedPortal, urlParams]); 
                
  // Auto-redirect to the appropriate portal when we end in an error state.
  // RMS links go to the sign-in page for the address they were heading to
  // (school, family or /rms/sign-in, TDI-704). A used or expired RMS reset
  // link stays put and says so, with its own Back to sign in (TDI-702).
  useEffect(() => {
    if (status === 'error') {
      if (isRmsResetLink) return undefined;
      let to = '/login';
      if (isRmsFlow) {
        to = signInPageForFailedLink(continueUrlParam);
      } else if (resolvedPortal === 'patient') {
        to = '/service-user/login';
      }
      const t = setTimeout(() => navigate(to), 1500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [status, resolvedPortal, navigate, isRmsFlow, isRmsResetLink, continueUrlParam]);

  // Gather User ID and organisation claims for support emails (best-effort)
  useEffect(() => {
    const u = auth?.currentUser;
    if (!u) return;
    setSupportCtx((p) => ({ ...p, userId: u.uid }));
    try {
      u.getIdTokenResult(true).then((res) => {
        const org = res?.claims?.organisation || '';
        setSupportCtx({ userId: u.uid, organisation: org });
      }).catch(() => {});
    } catch (_) {}
  }, []);

  // Determine the sign-in page to link to when MFA blocks passwordless sign-in.
  // RMS provider users go to /rms/provider/sign-in, ADHD service users go to
  // /service-user/login, and everyone else goes to /login.
  const passwordSignInPath = useMemo(() => {
    if (isRmsFlow) return '/rms/sign-in';
    const portal = resolvedPortal || suggestedPortal || 'provider';
    return portal === 'patient' ? '/service-user/login' : '/login';
  }, [isRmsFlow, resolvedPortal, suggestedPortal]);

  // Simple email validation for the cross-device sign-in form
  const isValidEmail = (emailStr) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailStr);

  // Conditional theming: RMS uses light gray minimal design, ADHD uses purple gradient
  const containerClass = isRmsFlow
    ? 'min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12'
    : 'min-h-screen flex items-center justify-center';
  const containerStyle = isRmsFlow
    ? {}
    : { backgroundImage: getBackgroundGradient() };
  const cardClass = isRmsFlow
    ? 'w-full max-w-sm'
    : 'w-full max-w-md bg-white rounded-2xl shadow-2xl drop-shadow-lg px-8 pb-8 mx-4';

  return (
    <div className={containerClass} style={containerStyle}>
      <div className={cardClass}>
        <div className="w-full text-center">
          {/* Environment banner - only show for non-RMS flows (RMS has cleaner look) */}
          {!isRmsFlow && showEnvBanner && (
            <div className={`-mx-8 mb-10 px-8 py-3 border font-semibold text-center rounded-t-2xl
              ${import.meta.env.VITE_ENVIRONMENT === "development" ? "bg-yellow-200 text-yellow-900 border-yellow-300" : "bg-blue-200 text-blue-900 border-blue-300"}
            `}>
              <div className="flex items-center justify-center gap-2">                
                <span>
                  {import.meta.env.VITE_ENVIRONMENT === "development" ? "Development Environment" : "Testing / Training Environment"}
                </span>
              </div>
            </div>
          )}
          
          {/* Logo - different sizing for RMS vs ADHD */}
          <img 
            src="/td_logo_full_2025.png" 
            alt="Logo" 
            className={isRmsFlow ? 'mx-auto mb-10' : `mx-auto mb-8 ${!showEnvBanner ? 'mt-10' : ''}`}
            style={{ width: isRmsFlow ? '180px' : '220px', height: 'auto' }} 
          />
          
          {status === 'verifying' && (
            <>
              <h2 className="text-2xl font-bold mb-2 text-center">
                {isRmsFlow ? 'Signing you in' : 'Verifying'}
              </h2>
              <p className="text-gray-600 mb-4 text-center">
                {isRmsFlow
                  ? 'This takes a few seconds. You do not need to tap the link again.'
                  : 'Please wait while we verify your request...'}
              </p>
              {/* TDI-506: a still page with no sign of life had parents on a
                  phone assuming it had failed and tapping the link a second
                  time, which invalidates the first. The bar shows the wait is
                  the system working, and it stops for anyone who has asked
                  their device to reduce motion. */}
              <div
                className="mx-auto mb-10 h-1 w-40 overflow-hidden rounded-full bg-gray-200 motion-reduce:bg-gray-300"
                role="status"
                aria-label={isRmsFlow ? 'Signing you in' : 'Verifying your request'}
              >
                <div className="h-full w-1/3 rounded-full bg-gray-400 animate-[verify-sweep_1.4s_ease-in-out_infinite] motion-reduce:w-full motion-reduce:animate-none" />
              </div>
            </>
          )}

          {status === 'confirmRevertMfa' && (
            <>
              <h2 className="text-2xl font-bold mb-2 text-center">
                Remove 2-Step Verification?
              </h2>
              <p className="text-gray-600 mb-4 text-center">
                You are about to remove 2-step verification from your account.
                Only do this if you did not add this security method yourself.
              </p>
              <p className="text-gray-500 text-sm mb-6 text-center">
                After removal, you will need to set up 2-step verification again the next time you sign in.
              </p>

              {revertMfaError && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 text-sm rounded-lg mb-4" role="alert">
                  {revertMfaError}
                </div>
              )}

              <button
                onClick={handleConfirmRevertMfa}
                disabled={isRevertingMfa}
                className={`w-full font-semibold py-2 rounded-lg transition duration-100 mb-3 ${
                  isRevertingMfa
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                {isRevertingMfa ? (
                  <span className="flex items-center justify-center">
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                    Removing...
                  </span>
                ) : (
                  'Confirm Removal'
                )}
              </button>

              <button
                onClick={() => {
                  const portal = resolvedPortal || suggestedPortal || 'provider';
                  navigate(portal === 'patient' ? '/service-user/login' : '/login');
                }}
                disabled={isRevertingMfa}
                className="w-full text-sm text-gray-500 hover:text-gray-700 py-2 transition duration-100"
              >
                Cancel &mdash; take me back to sign in
              </button>
            </>
          )}

          {status === 'resetPassword' && (
            <>
              <h2 className="text-2xl font-bold mb-2 text-center">
                Reset Your Password
              </h2>
              <p className="text-gray-600 mb-6 text-center">
                Please choose a new password for your account
              </p>
              <form onSubmit={handlePasswordReset} className="space-y-4">
                <div>
                  <div className="flex justify-between items-center">
                    <label className="block text-xs font-bold text-gray-700 mb-1 text-left">
                      New Password
                    </label>
                    <InfoTooltip
                      side="right"
                      content={"Your password needs to have 12 characters or more, and contain at least 1 number, 1 symbol and 1 upper & lower case letter"}
                    />
                  </div>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={handlePasswordChange}
                      className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary placeholder:text-sm"
                      placeholder="Enter new password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      <span className="relative h-5 w-5">
                        <EyeIcon
                          className={`absolute inset-0 h-5 w-5 text-gray-400 transition-all duration-150 ${
                            showPassword ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                          }`}
                        />
                        <EyeOffIcon
                          className={`absolute inset-0 h-5 w-5 text-gray-400 transition-all duration-150 ${
                            showPassword ? 'opacity-0 scale-75' : 'opacity-100 scale-100'
                          }`}
                        />
                      </span>
                    </button>
                  </div>
                  <PasswordRequirements value={password} onValidityChange={setIsPasswordValid} className="mt-2" />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1 text-left">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={handleConfirmPasswordChange}
                      className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary placeholder:text-sm"
                      placeholder="Confirm new password"
                      required
                      disabled={!password || !isPasswordValid}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      disabled={!password || !isPasswordValid}
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      <span className="relative h-5 w-5">
                        <EyeIcon
                          className={`absolute inset-0 h-5 w-5 text-gray-400 transition-all duration-150 ${
                            showConfirmPassword ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                          }`}
                        />
                        <EyeOffIcon
                          className={`absolute inset-0 h-5 w-5 text-gray-400 transition-all duration-150 ${
                            showConfirmPassword ? 'opacity-0 scale-75' : 'opacity-100 scale-100'
                          }`}
                        />
                      </span>
                    </button>
                  </div>
                  <div className={`mt-1 overflow-hidden transition-all duration-300 ${passwordMatch !== null ? 'max-h-6' : 'max-h-0'}`}>
                    <p className={`text-xs transition-opacity duration-300 ${passwordMatch !== null ? 'opacity-100' : 'opacity-0'} ${passwordMatch ? 'text-green-600' : 'text-red-600'}`}>
                      {passwordMatch ? 'Passwords match' : 'Passwords do not match'}
                    </p>
                  </div>
                </div>

                <div className={`transition-all duration-300 overflow-hidden ${error ? 'max-h-24 mt-2' : 'max-h-0 mt-0'}`} role="alert">
                  <div className={`bg-red-100 border border-red-400 text-red-700 px-4 py-3 text-sm rounded-lg relative ${error ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}>
                    {error}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={!isPasswordValid || !passwordMatch}
                  className={`w-full font-semibold py-2 rounded-lg transition duration-100 ${(!isPasswordValid || !passwordMatch) ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-primary text-white hover:bg-primary/90'}`}
                >
                  Reset Password
                </button>
              </form>
            </>
          )}

          {/* Cross-device email prompt for passwordless sign-in.
              Shown when the user opens the sign-in link on a different device/browser
              and we have no stored email. The user enters their email and we complete
              the sign-in. Themed to match the surrounding flow (RMS vs ADHD). */}
          {status === 'signInNeedsEmail' && (
            <>
              <h2 className="text-2xl font-bold mb-2 text-center">
                Confirm Your Email
              </h2>
              <p className="text-gray-600 mb-4 text-center">
                For security, please confirm the email address the sign-in link was sent to.
              </p>

              <div className="mb-4">
                <input
                  type="email"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-gray-900 text-base placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                  value={signInEmailInput}
                  onChange={e => setSignInEmailInput(e.target.value)}
                  placeholder="name@example.com"
                  autoFocus
                />
              </div>

              {!!error && (
                <div className="mb-4 text-sm text-red-600 bg-red-50 rounded-lg py-3 px-4">
                  {error}
                </div>
              )}

              <button
                onClick={handleCompleteSignInWithEmail}
                disabled={!isValidEmail(signInEmailInput) || signInBusy}
                className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all ${
                  (!isValidEmail(signInEmailInput) || signInBusy)
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-primary hover:bg-primary/90 shadow-md hover:shadow-lg'
                }`}
              >
                {signInBusy ? 'Completing...' : 'Complete Sign-in'}
              </button>
            </>
          )}

          {/* MFA blocks passwordless sign-in. The account has 2-step verification
              enrolled which requires the second factor during sign-in. Passwordless
              (email-link) sign-in can't present that challenge, so we direct the
              user to the password-based sign-in page instead. */}
          {status === 'signInMfaBlocked' && (
            <>
              <h2 className="text-2xl font-bold mb-2 text-center">
                2-Step Verification Required
              </h2>
              <p className="text-gray-600 mb-6 text-center">
                This account has 2-step verification enabled. Sign-in links cannot be used with 2-step verification. Please sign in with your password instead.
              </p>

              <button
                onClick={() => navigate(passwordSignInPath)}
                className="w-full py-3 px-4 rounded-lg font-semibold text-white bg-primary hover:bg-primary/90 shadow-md hover:shadow-lg transition-all"
              >
                Go to Sign In
              </button>
            </>
          )}

          {status === 'success' && (
            <>
              <h2 className="text-2xl font-bold mb-2 text-center">
                Success!
              </h2>
              <p className="text-gray-600 mb-10 text-center">
                {successKind === 'verifyEmail'
                  ? "Your email has been verified. Redirecting you to set up two-factor authentication..."
                  : successKind === 'revertMfa'
                    ? "Your 2-step verification has been removed. Redirecting you to sign in..."
                    : "Your password has been reset. Redirecting you to sign in..."}
              </p>
            </>
          )}

          {status === 'error' && isRmsResetLink && (
            <>
              <h2 className="text-2xl font-bold mb-2 text-center">
                This link has already been used or has expired.
              </h2>
              <p className="text-gray-600 mb-6 text-center">
                Request a new one from the sign-in page.
              </p>
              <button
                onClick={() => navigate(signInPageForFailedLink(continueUrlParam))}
                className="w-full py-3 px-4 rounded-lg font-semibold text-white bg-primary hover:bg-primary/90 shadow-md hover:shadow-lg transition-all"
              >
                Back to sign in
              </button>
            </>
          )}

          {status === 'error' && !isRmsResetLink && (
            <>
              <h2 className="text-2xl font-bold mb-2 text-center">
                {isRmsFlow ? 'Sign-in Link Expired' : 'Error'}
              </h2>
              <p className="text-gray-600 mb-2 text-center">
                {isRmsFlow 
                  ? `This sign-in link has expired or has already been used. Sign-in links can only be used once and work for ${SIGN_IN_LINK_LIFETIME}.`
                  : 'We couldn\'t process your request. The link may have expired or already been used.'
                }
              </p>
              <p className="text-gray-600 text-center">
                {isRmsFlow
                  ? 'Redirecting you to request a new sign-in link...'
                  : 'Redirecting you to sign in...'
                }
              </p>
            </>
          )}

          {/* Support footer - varies by portal type and RMS vs ADHD flow */}
          <div className="mt-6 text-center">
            {isRmsFlow ? (
              <p className="text-xs text-gray-500">Need help? Contact the school or service that invited you.</p>
            ) : (resolvedPortal || suggestedPortal) === 'provider' ? (
              (() => {
                const subject = 'Reporting Issue with ThinkDivergent - Verify Action';
                const now = new Date();
                const day = now.getDate();
                const sfx = (d) => {
                  if (d % 100 >= 11 && d % 100 <= 13) return 'th';
                  switch (d % 10) { case 1: return 'st'; case 2: return 'nd'; case 3: return 'rd'; default: return 'th'; }
                };
                const easyTime = `${day}${sfx(day)} ${now.toLocaleString('en-GB', { month: 'long' })} ${now.getFullYear()} at ${now.toLocaleString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
                const isoTime = now.toISOString();
                const lines = [
                  'To ThinkDivergent Support Team,',
                  '',
                  'I would like to report an issue on the platform.',
                  '',
                  '[Please describe briefly what you were doing and what happened]',
                  '',
                  'Helpful Information:',
                  `- Screen: ${typeof window !== 'undefined' ? window.location.pathname : ''}`,
                  `- URL: ${typeof window !== 'undefined' ? window.location.href : ''}`,
                  `- Time: ${easyTime} (${isoTime})`,
                  supportCtx.userId ? `- User ID: ${supportCtx.userId}` : undefined,
                  supportCtx.organisation ? `- Organisation: ${supportCtx.organisation}` : undefined,
                  error ? `- Message: ${error}` : undefined,
                  `- Browser: ${typeof navigator !== 'undefined' ? navigator.userAgent : ''}`
                ].filter(v => v != null).join('\n');
                const href = `mailto:support@thinkdivergent.co.uk?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines)}`;
                return (
                  <p className="text-xs text-gray-500">
                    Need help? Contact your organisation administrator or {""}
                    <a href={href} className="underline text-primary">report an issue</a>.
                  </p>
                );
              })()
            ) : (
              <p className="text-xs text-gray-500">Need help? Contact your healthcare provider.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
